import _ from 'lodash'
import React, { useCallback } from 'react'

import { getFormBuilderStructure } from 'services/formBuilderSettings/constants'
import {
  FormBuilderField,
  FormBuilderGroup,
  FormBuilderSection,
  FormBuilderSubGroup,
  FormTypes,
} from 'services/formBuilderSettings/models'
import { NurserySettingsFormBuilder } from 'services/nurseries/models'

import { generateRoute } from 'utils/routing'

import { Banner, Flex, Grid, Page, Section, Space, Typography } from 'components'

import i18n from 'translations'

import Field from './components/Field/Field'
import { StyledContent, StyledGroupItem, StyledSidebarItem } from './FormBuilderStyled'

interface FormBuilderViewProps {
  isFetching: boolean
  isInitialLoading: boolean
  navigation: {
    group: FormBuilderGroup | null
    section: FormBuilderSection
  }
  nurseryFormBuilderSettings: NurserySettingsFormBuilder
  onFieldChange: (value: {
    field: FormBuilderField
    formType?: FormTypes
    value: any
  }) => void
  onNavigate: (section: FormBuilderSection, group?: FormBuilderGroup) => void
}

const FormBuilderView = ({
  isFetching,
  isInitialLoading,
  navigation,
  nurseryFormBuilderSettings,
  onFieldChange,
  onNavigate,
}: FormBuilderViewProps) => {
  const formBuilderStructure = getFormBuilderStructure(i18n)

  const renderBody = useCallback(() => {
    const selectedSection = _.find(formBuilderStructure, { key: navigation.section.key })
    let selectedGroup = null

    if (1 === selectedSection.groups.length) {
      selectedGroup = selectedSection.groups[0]
    } else if (navigation.group) {
      selectedGroup = _.find(selectedSection.groups, { key: navigation.group.key })
    }

    return (
      <React.Fragment>
        {!selectedSection?.hideHeader && (
          <Grid spacing={0}>
            <Grid.Item alignItems={{ desktop: 'center' }} flex={{ desktop: 1 }}>
              <Typography padding="8px 0" bold>
                {selectedGroup?.label || selectedSection.label}
              </Typography>
            </Grid.Item>
            {/* TODO: V2 */}
            {/* <Grid.Item width={{ desktop: '80px' }}>
              <Typography align="right" fontSize={14} padding="8px 0">
                {i18n.t('module:Management:FormBuilder:enquiryForm')}
              </Typography>
            </Grid.Item> */}
            <Grid.Item width={{ desktop: '150px' }}>
              <Typography align="right" fontSize={14} padding="8px 16px 8px 0">
                {i18n.t('module:Management:FormBuilder:registrationForm')}
              </Typography>
            </Grid.Item>
          </Grid>
        )}
        {_.map(selectedGroup.subGroups, (subGroup: FormBuilderSubGroup) => (
          <React.Fragment key={subGroup.label}>
            {subGroup.label && (
              <Flex padding={selectedSection?.hideHeader ? '12px 0' : '12px 24px'}>
                <Typography bold={selectedSection?.hideHeader}>
                  {subGroup.label}
                </Typography>
              </Flex>
            )}
            {_.map(subGroup.fields, (field: FormBuilderField) => (
              <Field
                field={field}
                isFetching={isFetching}
                key={field.key}
                nurseryFormBuilderSettings={nurseryFormBuilderSettings}
                onChange={onFieldChange}
              />
            ))}
            <Space margin="0 0 8px" />
          </React.Fragment>
        ))}
      </React.Fragment>
    )
  }, [navigation, isFetching, nurseryFormBuilderSettings, onFieldChange])

  return (
    <Page.Section
      actions={(
        <Section.Actions
          tertiary={{
            icon: 'eye',
            label: i18n.t('module:Management:FormBuilder:preview'),
            to: generateRoute('MANAGEMENT.FORM_BUILDER.PREVIEW'),
          }}
        />
      )}
      isLoading={isInitialLoading}
      title={i18n.t('module:Management:FormBuilder:title')}
      noBorder
    >
      {nurseryFormBuilderSettings
      && (!nurseryFormBuilderSettings.agreement || ' ' === nurseryFormBuilderSettings.agreement) && (
        <Banner.Warning>
          {i18n.t('module:Management:FormBuilder:agreementSectionEmpty')}
        </Banner.Warning>
      )}
      {nurseryFormBuilderSettings
      && (!nurseryFormBuilderSettings.termsAndConditions || ' ' === nurseryFormBuilderSettings.termsAndConditions) && (
        <Banner.Warning>
          {i18n.t('module:Management:FormBuilder:termsAndConditionsSectionEmpty')}
        </Banner.Warning>
      )}
      <Grid spacing={8}>
        <Grid.Item width={{ desktop: '248px' }}>
          {_.map(formBuilderStructure, (section: FormBuilderSection) => (
            <React.Fragment key={section.label}>
              <StyledSidebarItem
                $active={navigation.section.key === section.key && 1 >= section.groups?.length}
                key={section.label}
                onClick={() => onNavigate(section)}
              >
                <Typography fontSize={12} transform="uppercase">
                  {section.label}
                </Typography>
              </StyledSidebarItem>
              {1 < section.groups?.length && (
                <Flex flexDirection="column" margin="0 0 10px 0">
                  {_.map(section.groups, (group: FormBuilderGroup) => (
                    <StyledGroupItem
                      $active={navigation.group?.key === group.key && navigation.section.key === section.key}
                      key={group.label}
                      onClick={() => onNavigate(section, group)}
                    >
                      <Typography fontSize={14}>
                        {group.label}
                      </Typography>
                    </StyledGroupItem>
                  ))}
                </Flex>
              )}
            </React.Fragment>
          ))}
        </Grid.Item>
        <Grid.Item flex={{ desktop: 1 }}>
          <StyledContent>
            {renderBody()}
          </StyledContent>
        </Grid.Item>
      </Grid>
    </Page.Section>
  )
}

export default FormBuilderView
